/**
 * Avatar-layer rendering — procedural-fallback per Q-C8 / CLIENT_DESIGN §5.
 *
 * No sprite atlas ships in Phase 1, so each player renders as a small
 * coloured capsule: palette-tinted body + facing-triangle pip + display-
 * name label. Real 8-directional sprite frames replace this in Phase 4
 * polish; the character layer already has `sortableChildren = true` so
 * the atlas swap-in is drop-in (each entity keeps its `depthSortKey`
 * zIndex).
 *
 * Movement interpolation per §8.2: the server sends integer tile positions
 * at `tick_hz` Hz; the client lerps the avatar's rendered position from
 * its previous to its current tile over `1000 / tick_hz` ms so movement
 * reads smoothly at 60 fps. Phase 1 is visual-only — no client-side
 * prediction (decision 23.1-#8).
 */

import { Container, Graphics, Text } from 'pixi.js';

import type { PlayerEntry, WorldModel } from '../world/model';
import { depthSortKey, tileToIso, Z_CHARACTER, type CameraState } from './iso';
import type { SceneLayers } from './scene';

const AVATAR_BODY_W = 28;
const AVATAR_BODY_H = 48;
const AVATAR_HEAD_R = 10;
const SELF_HIGHLIGHT_RING = 0xffcc33;

/**
 * Per-player interpolation state. Kept separate from the authoritative
 * `WorldModel` so the store stays wire-mirroring pure; the renderer owns
 * the visual-only slice.
 */
interface InterpState {
  /** Previous tile position — start of the current lerp. */
  fromX: number;
  fromY: number;
  /** Target tile position — end of the current lerp. */
  toX: number;
  toY: number;
  /** `performance.now()` at the moment the lerp began. */
  startedAtMs: number;
}

export interface AvatarRenderer {
  render: (
    model: WorldModel,
    cam: CameraState,
    selfPlayerId: bigint | null,
    tickHz: number,
    nowMs: number,
  ) => void;
  destroy: () => void;
}

interface AvatarNode {
  container: Container;
  body: Graphics;
  head: Graphics;
  pip: Graphics;
  nameLabel: Text;
  selfRing: Graphics;
  interp: InterpState;
}

export function createAvatarRenderer(layers: SceneLayers): AvatarRenderer {
  const pool = new Map<bigint, AvatarNode>();
  // `layers.characterLayer` is sortableChildren; we set zIndex per node
  // from depthSortKey each frame.

  const render = (
    model: WorldModel,
    cam: CameraState,
    selfPlayerId: bigint | null,
    tickHz: number,
    nowMs: number,
  ): void => {
    void cam; // camera transform already applied on the world container
    const seen = new Set<bigint>();
    const tickMs = Math.max(1, 1000 / Math.max(1, tickHz));

    for (const player of model.players.values()) {
      let node = pool.get(player.id);
      if (!node) {
        node = createAvatarNode(player, nowMs);
        layers.characterLayer.addChild(node.container);
        pool.set(player.id, node);
      }

      updateInterpTarget(node, player, nowMs);

      const progress = Math.min(
        1,
        Math.max(0, (nowMs - node.interp.startedAtMs) / tickMs),
      );
      const lx = node.interp.fromX + (node.interp.toX - node.interp.fromX) * progress;
      const ly = node.interp.fromY + (node.interp.toY - node.interp.fromY) * progress;

      const iso = tileToIso(lx, ly);
      node.container.position.set(iso.x, iso.y);
      // Depth-sort by target tile (not lerped) so the in-flight avatar
      // slot matches the destination tile for occlusion intent.
      node.container.zIndex = depthSortKey(
        node.interp.toX,
        node.interp.toY,
        Z_CHARACTER,
      );

      // Body + head + facing pip are static per-frame; update text only
      // on name / role / self changes.
      if (node.nameLabel.text !== player.display_name) {
        node.nameLabel.text = player.display_name;
      }
      const isSelf = selfPlayerId !== null && player.id === selfPlayerId;
      node.selfRing.visible = isSelf;

      // Rotate / orient the facing pip per the player's cardinal facing.
      node.pip.angle = facingAngleDeg(player.facing);

      // Dimmed when disconnected (§4.9 PlayerDisconnected behaviour).
      node.container.alpha = player.connected ? 1 : 0.5;

      seen.add(player.id);
    }

    // Cull any avatar whose player dropped from the model.
    for (const [id, node] of pool) {
      if (seen.has(id)) continue;
      node.container.destroy({ children: true });
      pool.delete(id);
    }
  };

  return {
    render,
    destroy: () => {
      for (const node of pool.values()) {
        node.container.destroy({ children: true });
      }
      pool.clear();
    },
  };
}

function createAvatarNode(player: PlayerEntry, nowMs: number): AvatarNode {
  const container = new Container({ label: `avatar-${player.id}` });

  const selfRing = new Graphics();
  selfRing.circle(0, -AVATAR_BODY_H / 2, AVATAR_BODY_W * 0.9);
  selfRing.stroke({ color: SELF_HIGHLIGHT_RING, width: 3, alpha: 0.9 });
  selfRing.visible = false;

  const body = new Graphics();
  // Capsule body — rounded rectangle centred on the tile.
  body.roundRect(
    -AVATAR_BODY_W / 2,
    -AVATAR_BODY_H,
    AVATAR_BODY_W,
    AVATAR_BODY_H,
    AVATAR_BODY_W / 2,
  );
  body.fill({ color: avatarBodyColor(player.id), alpha: 1 });
  body.stroke({ color: 0x000000, width: 1, alpha: 0.4 });

  const head = new Graphics();
  head.circle(0, -AVATAR_BODY_H - AVATAR_HEAD_R + 2, AVATAR_HEAD_R);
  head.fill({ color: avatarSkinColor(player.id), alpha: 1 });
  head.stroke({ color: 0x000000, width: 1, alpha: 0.4 });

  // Facing pip — small triangle that rotates to indicate cardinal facing.
  const pip = new Graphics();
  pip.poly([0, -6, -5, 4, 5, 4]);
  pip.fill({ color: 0xffffff, alpha: 0.85 });
  pip.position.set(0, -AVATAR_BODY_H - AVATAR_HEAD_R + 2);

  const nameLabel = new Text({
    text: player.display_name,
    style: {
      fill: 0xffffff,
      fontSize: 11,
      fontFamily: 'sans-serif',
      stroke: { color: 0x000000, width: 2 },
    },
  });
  nameLabel.anchor.set(0.5, 1);
  nameLabel.position.set(0, -AVATAR_BODY_H - AVATAR_HEAD_R * 2 - 2);

  container.addChild(selfRing, body, head, pip, nameLabel);

  return {
    container,
    body,
    head,
    pip,
    nameLabel,
    selfRing,
    interp: {
      fromX: player.position[0],
      fromY: player.position[1],
      toX: player.position[0],
      toY: player.position[1],
      startedAtMs: nowMs,
    },
  };
}

/** Kick off a new lerp whenever the authoritative model position changes.
 *  Single-tile moves animate; multi-tile jumps (snapshot rehydration,
 *  replay catch-up, future warp events) snap to the new position so the
 *  avatar doesn't slide across the world from a stale start. */
function updateInterpTarget(
  node: AvatarNode,
  player: PlayerEntry,
  nowMs: number,
): void {
  const [mx, my] = player.position;
  if (mx === node.interp.toX && my === node.interp.toY) return;
  const dx = Math.abs(mx - node.interp.toX);
  const dy = Math.abs(my - node.interp.toY);
  if (dx > 1 || dy > 1) {
    node.interp.fromX = mx;
    node.interp.fromY = my;
    node.interp.toX = mx;
    node.interp.toY = my;
    node.interp.startedAtMs = nowMs;
    return;
  }
  // Normal cardinal-step lerp: take the currently-rendered position as
  // the new start so back-to-back PlayerMoved events don't visibly snap.
  node.interp.fromX = node.interp.toX;
  node.interp.fromY = node.interp.toY;
  node.interp.toX = mx;
  node.interp.toY = my;
  node.interp.startedAtMs = nowMs;
}

function facingAngleDeg(facing: PlayerEntry['facing']): number {
  // Screen-relative: North = "up on screen", which in iso is pointing
  // toward decreasing (x+y). Map cardinal → screen-angle rotation.
  switch (facing) {
    case 'North':
      return 0;
    case 'East':
      return 90;
    case 'South':
      return 180;
    case 'West':
      return 270;
  }
}

/** Deterministic body colour until a real `AvatarConfig.palette` arrives. */
function avatarBodyColor(id: number | bigint): number {
  const palette = [0x4a5d7a, 0x8b5a2b, 0x6b5e88, 0x5b8c52, 0x8b3a3a, 0xc6a664];
  return palette[Number(id) % palette.length] ?? palette[0]!;
}

function avatarSkinColor(id: number | bigint): number {
  const palette = [0xe8c88c, 0xd9a474, 0xc18f5a, 0x9e6d48, 0x6f4a2f];
  return palette[Number(id) % palette.length] ?? palette[0]!;
}
